import type { BlinkEvent } from "./types.ts";

/** The reference range for a resting adult, per the ophthalmology literature. */
export const HEALTHY_RATE_RANGE: readonly [number, number] = [15, 20];

export interface SessionMetrics {
  /** Complete blinks in the trailing 60 seconds, extrapolated to a per-minute rate. */
  ratePerMinute: number;
  totalComplete: number;
  totalPartial: number;
  /** Share of all detected closures that were partial, 0..1. */
  partialShare: number;
  meanDurationMs: number;
  medianDurationMs: number;
  /** Milliseconds since the last complete blink. */
  sinceLastBlinkMs: number;
  /** The longest the subject went without a complete blink this session. */
  longestGapMs: number;
  meanLatencyMs: number;
  sessionDurationMs: number;
  /**
   * Cumulative time the signal read as closed, in-progress closure included.
   *
   * Accumulated from frames rather than summed from BlinkEvent durations, and
   * the difference matters: this counts closure the detector rejected — the
   * too-long, the too-jittery, the sustained squint — so a session where the
   * eyes are shut a lot but nothing is emitted reads as busy here and silent
   * everywhere else. That gap is the interesting failure, and summing events
   * would hide it by construction.
   */
  closedMs: number;
  /** closedMs as a share of the session, 0..1. */
  closedShare: number;
}

const EMPTY: SessionMetrics = {
  ratePerMinute: 0,
  totalComplete: 0,
  totalPartial: 0,
  partialShare: 0,
  meanDurationMs: 0,
  medianDurationMs: 0,
  sinceLastBlinkMs: 0,
  longestGapMs: 0,
  meanLatencyMs: 0,
  sessionDurationMs: 0,
  closedMs: 0,
  closedShare: 0,
};

/**
 * Longest plausible gap between frames to attribute to the closed state.
 *
 * A dropped frame, a backgrounded tab or a stalled camera leaves a hole in the
 * timeline; charging the whole hole to "eyes closed" because the last sample
 * before it happened to be mid-blink would turn a hiccup into several seconds
 * of apparent closure. Beyond this the interval is simply not counted.
 */
const MAX_CLOSED_STEP_MS = 250;

/**
 * Session statistics, derived purely from the event stream.
 *
 * Note it consumes BlinkEvents, not frames — the same aggregate can therefore be
 * computed live from a camera or offline from a fixture and get the same answer,
 * which is what makes the numbers in the UI and the numbers in `npm run eval`
 * comparable.
 */
export class SessionMetricsTracker {
  #complete: BlinkEvent[] = [];
  #partialCount = 0;
  #startedAt: number | null = null;
  #now = 0;
  #longestGap = 0;
  #lastCompleteAt: number | null = null;
  #closedMs = 0;
  #lastTickAt: number | null = null;
  #wasClosed = false;

  /**
   * Call every frame so elapsed-time metrics advance even when nothing happens.
   *
   * @param closed whether the eyes read as closed on THIS frame. The interval
   *   since the previous frame is charged to the previous frame's state — a
   *   zero-order hold, which is the only honest reading of a sampled signal.
   */
  tick(t: number, closed = false): void {
    this.#startedAt ??= t;
    this.#now = t;
    if (this.#lastCompleteAt !== null) {
      this.#longestGap = Math.max(this.#longestGap, t - this.#lastCompleteAt);
    }

    if (this.#lastTickAt !== null && this.#wasClosed) {
      const step = t - this.#lastTickAt;
      if (step > 0 && step <= MAX_CLOSED_STEP_MS) this.#closedMs += step;
    }
    this.#lastTickAt = t;
    this.#wasClosed = closed;
  }

  add(event: BlinkEvent): void {
    this.#startedAt ??= event.t;
    this.#now = Math.max(this.#now, event.t);

    if (event.kind === "partial") {
      this.#partialCount++;
      return;
    }
    if (this.#lastCompleteAt !== null) {
      this.#longestGap = Math.max(this.#longestGap, event.t - this.#lastCompleteAt);
    }
    this.#lastCompleteAt = event.t;
    this.#complete.push(event);
  }

  snapshot(): SessionMetrics {
    if (this.#startedAt === null) return EMPTY;

    const elapsed = this.#now - this.#startedAt;
    const windowStart = this.#now - 60_000;
    const inWindow = this.#complete.filter((e) => e.t >= windowStart);

    // Early in a session a 60s window is mostly empty, so a raw count would read
    // as an implausibly low rate. Extrapolate over the time actually observed
    // until a full minute exists, and only then use the true sliding count.
    const observed = Math.min(elapsed, 60_000);
    const ratePerMinute = observed > 2000 ? (inWindow.length * 60_000) / observed : 0;

    const durations = this.#complete.map((e) => e.duration);
    const total = this.#complete.length + this.#partialCount;

    return {
      ratePerMinute,
      totalComplete: this.#complete.length,
      totalPartial: this.#partialCount,
      partialShare: total > 0 ? this.#partialCount / total : 0,
      meanDurationMs: mean(durations),
      medianDurationMs: median(durations),
      sinceLastBlinkMs: this.#lastCompleteAt === null ? elapsed : this.#now - this.#lastCompleteAt,
      longestGapMs: this.#longestGap,
      meanLatencyMs: mean(this.#complete.map((e) => e.latency)),
      sessionDurationMs: elapsed,
      closedMs: this.#closedMs,
      closedShare: elapsed > 0 ? this.#closedMs / elapsed : 0,
    };
  }

  reset(): void {
    this.#complete = [];
    this.#partialCount = 0;
    this.#startedAt = null;
    this.#now = 0;
    this.#longestGap = 0;
    this.#lastCompleteAt = null;
    this.#closedMs = 0;
    this.#lastTickAt = null;
    this.#wasClosed = false;
  }
}

export function mean(values: readonly number[]): number {
  if (values.length === 0) return 0;
  let sum = 0;
  for (const v of values) sum += v;
  return sum / values.length;
}

export function median(values: readonly number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = sorted.length >> 1;
  return sorted.length % 2
    ? (sorted[mid] as number)
    : ((sorted[mid - 1] as number) + (sorted[mid] as number)) / 2;
}

export function quantile(values: readonly number[], q: number): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const idx = Math.min(sorted.length - 1, Math.max(0, Math.round(q * (sorted.length - 1))));
  return sorted[idx] as number;
}
